import React from 'react';
import { Trophy, ArrowLeft } from 'lucide-react';
import { useLeaderboard } from '../hooks/useLeaderboardData';
import { RankBadge } from '../components/RankBadge';
import { formatVolume, formatNumber } from '../utils/formatters';

const Champions: React.FC = () => {
  const { data: users, loading, error, refresh } = useLeaderboard({ filter: 'overall', limit: 100 });


  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => window.location.hash = '/'}
            className="btn-secondary p-2 rounded-lg hover:bg-white/20 transition-all"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-white flex items-center gap-3">
              <Trophy className="w-8 h-8 text-yellow-400" />
              Champions Leaderboard
            </h1>
            <p className="text-slate-300 mt-2">
              Complete ranking of all bridge users on the Shido Network
            </p>
          </div>
          <button
            onClick={refresh}
            className="btn-primary px-4 py-2 rounded-lg ml-auto"
          >
            Refresh
          </button>
        </div>

        {/* Champions Table */}
        <div className="glass-card p-6">
          {loading ? (
            <div className="space-y-4">
              {[...Array(10)].map((_, i) => (
                <div key={i} className="loading-shimmer h-20 rounded-lg"></div>
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <Trophy className="w-12 h-12 text-red-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-red-400 mb-2">Failed to Load</h3>
              <p className="text-slate-400 mb-4">{error}</p>
              <button
                onClick={refresh}
                className="btn-primary px-6 py-2 rounded-lg"
              >
                Try Again
              </button>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12">
              <Trophy className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-400 mb-2">No Champions Yet</h3>
              <p className="text-slate-500">
                Champions will appear here as users bridge tokens
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">
                  Top {users.length} Champions
                </h2>
                <div className="text-sm text-slate-400">
                  Auto-refreshes every 30 seconds
                </div>
              </div>

              {users.map((user, index) => (
                <div
                  key={user.address}
                  className="flex items-center justify-between p-4 rounded-lg bg-white/5 hover:bg-white/10 transition-all group animate-slide-up"
                  style={{ animationDelay: `${index * 20}ms` }}
                >
                  <div className="flex items-center gap-4">
                    <RankBadge rank={index + 1} />
                    
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span 
                          className="font-mono text-sm text-white group-hover:text-blue-300 transition-colors cursor-pointer"
                          onClick={() => window.location.hash = `/user/${user.address}`}
                        >
                          {user.displayAddress}
                        </span>
                        {index < 3 && (
                          <span className="text-xs px-2 py-1 rounded-full text-yellow-300 bg-white/10">
                            {index === 0 ? 'CHAMPION' : index === 1 ? 'RUNNER-UP' : 'THIRD'}
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-4 text-sm text-slate-400">
                        <span>{formatNumber(user.transactionCount)} transactions</span>
                        <span>Avg: {formatVolume(user.averageTransactionSize)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="text-right">
                    <div className="text-lg font-bold text-white">
                      {formatVolume(user.totalVolume)}
                    </div>
                    <div className="text-sm text-slate-400">
                      Total Volume
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Back to Home */}
        <div className="text-center mt-8">
          <button
            onClick={() => window.location.hash = '/'}
            className="btn-secondary px-6 py-3 rounded-lg"
          >
            ← Back to Home
          </button>
        </div>
      </div>
    </div>
  );
};

export default Champions;
